import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import {
  type DateWindow,
  type PanelDimensions,
  type SelectedFilters,
  useFilterCatalog,
  useMetrics,
  usePanelValues,
} from '../../lib/filters.hooks.js';
import { useTRPC } from '../../lib/trpc.js';
import { NAME_DROPDOWN_CAP } from '../filters/nameCaps.js';
import type { ScopeOption } from '../filters/types.js';
import type { FieldValues } from './fieldValues.js';
import type { WorkflowField, WorkflowStep } from './workflows.types.js';

type Dimension = NonNullable<PanelDimensions>[number];
type ValuesVia = 'filter_panel' | 'filter_names';

const SEARCH_DEBOUNCE_MS = 300;

export interface FieldOptionsResult {
  options: ScopeOption[];
  /** The rest of the list, behind "See more", when the field leads with a short one. */
  more?: ScopeOption[];
  loading: boolean;
  unsupported: boolean;
  onSearchChange: (text: string) => void;
}

export type FieldOptions = (field: WorkflowField) => FieldOptionsResult;

interface Lookup {
  key: Dimension | undefined;
  /** Which value call serves the key on this scope; undefined until the catalog has loaded. */
  via: ValuesVia | null | undefined;
  // A dependent field's picks are its own answer, not a narrowing of its siblings.
  narrows: boolean;
}

function narrowed(
  options: ScopeOption[],
  named: string[],
  leadingKpi: string | undefined,
): ScopeOption[] {
  const kept = options.filter((option) => named.includes(option.id) || option.id === leadingKpi);
  const leading = kept.find((option) => option.id === leadingKpi);
  return leading === undefined ? kept : [leading, ...kept.filter((option) => option !== leading)];
}

// The three kinds that draw from a source rather than a fixed list.
function isSourced(
  field: WorkflowField,
): field is Extract<WorkflowField, { kind: 'select' | 'segmented' | 'multiselect' }> {
  return field.kind === 'select' || field.kind === 'segmented' || field.kind === 'multiselect';
}

function sourceKeyOf(field: WorkflowField, values: FieldValues): Dimension | undefined {
  if (!isSourced(field)) return undefined;
  const dependent = field.optionsSource?.filterFrom;
  if (dependent == null) return field.optionsSource?.filter ?? undefined;
  const chosen = values[dependent.field];
  return typeof chosen === 'string' ? dependent.filters[chosen] : undefined;
}

function lookupsOf(
  step: WorkflowStep | undefined,
  values: FieldValues,
  routes: ReadonlyMap<string, ValuesVia | null> | undefined,
): Map<string, Lookup> {
  const leaves = (step?.fields ?? []).flatMap((field) =>
    field.kind === 'collapsible' ? field.fields : [field],
  );
  return new Map(
    leaves.map((field) => {
      const key = sourceKeyOf(field, values);
      const via = key === undefined || routes === undefined ? undefined : (routes.get(key) ?? null);
      const narrows = !isSourced(field) || field.optionsSource?.filterFrom == null;
      return [field.key, { key, via, narrows }];
    }),
  );
}

function keysOf(lookups: Map<string, Lookup>, via: ValuesVia): Dimension[] {
  return [
    ...new Set(
      [...lookups.values()].flatMap((lookup) =>
        lookup.key !== undefined && lookup.via === via ? [lookup.key] : [],
      ),
    ),
  ];
}

export function useFieldOptions(
  step: WorkflowStep | undefined,
  values: FieldValues,
  dateWindow: DateWindow,
  accountIds: number[],
  leadingKpi?: string,
): FieldOptions {
  const trpc = useTRPC();
  const [nameSearch, setNameSearch] = useState<Partial<Record<Dimension, string>>>({});
  const catalog = useFilterCatalog(accountIds);
  const routes = useMemo(
    () =>
      catalog.data === undefined
        ? undefined
        : new Map(catalog.data.map((entry) => [entry.key as string, entry.valuesVia])),
    [catalog.data],
  );
  const lookups = useMemo(() => lookupsOf(step, values, routes), [step, values, routes]);
  const panelKeys = useMemo(() => keysOf(lookups, 'filter_panel'), [lookups]);
  const nameKeys = useMemo(() => keysOf(lookups, 'filter_names'), [lookups]);

  const selected = useMemo(() => {
    const chosen: SelectedFilters = {};
    for (const [fieldKey, lookup] of lookups) {
      const value = values[fieldKey];
      if (
        lookup.key === undefined ||
        !lookup.narrows ||
        !Array.isArray(value) ||
        value[0] === undefined
      )
        continue;
      chosen[lookup.key] = value[0];
    }
    return chosen;
  }, [lookups, values]);

  const searched = nameKeys.filter((key) => (nameSearch[key] ?? '') !== '');
  const caps = Object.fromEntries(
    nameKeys.filter((key) => !searched.includes(key)).map((key) => [key, NAME_DROPDOWN_CAP]),
  );

  const metrics = useMetrics(accountIds);
  const panel = usePanelValues(panelKeys, selected, dateWindow, accountIds);
  const names = useQuery({
    ...trpc.filters.names.queryOptions({
      names: nameKeys,
      dateWindow,
      accountIds,
      caps,
      ...(searched.length > 0 ? { nameSearch } : {}),
    }),
    enabled: nameKeys.length > 0,
    placeholderData: (previous) => previous,
  });

  const searchFor = useMemo(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    return (key: Dimension, text: string): void => {
      clearTimeout(timer);
      timer = setTimeout(
        () => setNameSearch((current) => ({ ...current, [key]: text })),
        SEARCH_DEBOUNCE_MS,
      );
    };
  }, []);

  return useMemo(() => {
    const metricOptions: ScopeOption[] = Object.values(metrics.data ?? {})
      .filter((metric) => metric.synthetic !== true)
      .map((metric) => ({ id: metric.name, label: metric.label }));
    const noop = (): void => {};
    const settled = (options: ScopeOption[]): FieldOptionsResult => ({
      options,
      loading: false,
      unsupported: false,
      onSearchChange: noop,
    });
    return (field) => {
      if (!isSourced(field)) return settled([]);
      if (field.optionsSource === null) return settled(field.options);
      if (field.optionsSource.metric === true) {
        const named = field.optionsSource.metricNames;
        if (named === null) {
          return {
            options: metricOptions,
            loading: metrics.isPending,
            unsupported: false,
            onSearchChange: noop,
          };
        }
        const options = narrowed(metricOptions, named, leadingKpi);
        return {
          options,
          more: metricOptions
            .filter((option) => !options.includes(option))
            .sort((a, b) => a.label.localeCompare(b.label)),
          loading: metrics.isPending,
          unsupported: false,
          onSearchChange: noop,
        };
      }
      const lookup = lookups.get(field.key);
      const key = lookup?.key;
      if (lookup === undefined || key === undefined || lookup.via === null) {
        return { options: [], loading: false, unsupported: true, onSearchChange: noop };
      }
      if (lookup.via === undefined) {
        return { options: [], loading: true, unsupported: false, onSearchChange: noop };
      }
      const panelServed = lookup.via === 'filter_panel';
      const source = panelServed ? panel : names;
      const dimension = source.data?.[key];
      return {
        options: (dimension?.values ?? []).map((value) => ({
          id: value,
          label: dimension?.labels?.[value] ?? value,
        })),
        loading: source.isPending,
        unsupported: false,
        onSearchChange: panelServed ? noop : (text: string) => searchFor(key, text),
      };
    };
  }, [lookups, metrics.data, metrics.isPending, panel, names, searchFor, leadingKpi]);
}
